// src/api/projectsApi.ts

import { baseApi } from './baseApi';
import type {
  Project,
  ProjectMilestone,
  ProjectDeliverable,
  ProjectContact,
  CreateProjectRequest,
  UpdateProjectRequest,
  CreateMilestoneRequest,
  UpdateMilestoneRequest,
  CreateDeliverableRequest,
  UpdateDeliverableRequest,
  AddProjectContactRequest,
  UpdateProjectContactRequest,
  ProjectStatus,
} from '../types/api';

interface GetProjectsParams {
  workspaceId?: string;
  q?: string;
  status?: ProjectStatus;
  pocContactId?: string;
}

export const projectsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProjects: builder.query<Project[], GetProjectsParams>({
      query: (params) => ({
        url: '/projects',
        params: {
          ...(params.workspaceId && { workspaceId: params.workspaceId }),
          ...(params.q && { q: params.q }),
          ...(params.status && { status: params.status }),
          ...(params.pocContactId && { pocContactId: params.pocContactId }),
        },
      }),
      providesTags: (result, error, { workspaceId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Project' as const, id })),
              { type: 'Project', id: workspaceId || 'LIST' },
            ]
          : [{ type: 'Project', id: workspaceId || 'LIST' }],
    }),

    getProject: builder.query<Project, string>({
      query: (id) => `/projects/${id}`,
      providesTags: (result, error, id) => [{ type: 'Project', id }],
    }),

    createProject: builder.mutation<Project, CreateProjectRequest>({
      query: (body) => ({
        url: '/projects',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { workspaceId }) => [
        { type: 'Project', id: workspaceId || 'LIST' },
      ],
    }),

    updateProject: builder.mutation<Project, { id: string } & UpdateProjectRequest>({
      query: ({ id, ...body }) => ({
        url: `/projects/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Project', id },
        { type: 'Project', id: 'LIST' },
      ],
    }),

    deleteProject: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/projects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Project', id },
        { type: 'Project', id: 'LIST' },
      ],
    }),

    // Milestones
    createMilestone: builder.mutation<ProjectMilestone, { projectId: string } & CreateMilestoneRequest>({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/milestones`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Project', id: projectId },
      ],
    }),

    updateMilestone: builder.mutation<ProjectMilestone, { projectId: string; milestoneId: string } & UpdateMilestoneRequest>({
      query: ({ projectId, milestoneId, ...body }) => ({
        url: `/projects/${projectId}/milestones/${milestoneId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Project', id: projectId },
      ],
    }),

    deleteMilestone: builder.mutation<{ success: boolean }, { projectId: string; milestoneId: string }>({
      query: ({ projectId, milestoneId }) => ({
        url: `/projects/${projectId}/milestones/${milestoneId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Project', id: projectId },
      ],
    }),

    // Deliverables
    createDeliverable: builder.mutation<ProjectDeliverable, { projectId: string } & CreateDeliverableRequest>({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/deliverables`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Project', id: projectId },
      ],
    }),

    updateDeliverable: builder.mutation<ProjectDeliverable, { projectId: string; deliverableId: string } & UpdateDeliverableRequest>({
      query: ({ projectId, deliverableId, ...body }) => ({
        url: `/projects/${projectId}/deliverables/${deliverableId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Project', id: projectId },
      ],
    }),

    deleteDeliverable: builder.mutation<{ success: boolean }, { projectId: string; deliverableId: string }>({
      query: ({ projectId, deliverableId }) => ({
        url: `/projects/${projectId}/deliverables/${deliverableId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Project', id: projectId },
      ],
    }),

    // Project Contacts
    addProjectContact: builder.mutation<ProjectContact, { projectId: string } & AddProjectContactRequest>({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/contacts`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Project', id: projectId },
      ],
    }),

    updateProjectContact: builder.mutation<ProjectContact, { projectId: string; projectContactId: string } & UpdateProjectContactRequest>({
      query: ({ projectId, projectContactId, ...body }) => ({
        url: `/projects/${projectId}/contacts/${projectContactId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Project', id: projectId },
      ],
    }),

    removeProjectContact: builder.mutation<{ success: boolean }, { projectId: string; projectContactId: string }>({
      query: ({ projectId, projectContactId }) => ({
        url: `/projects/${projectId}/contacts/${projectContactId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Project', id: projectId },
      ],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useGetProjectQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useCreateMilestoneMutation,
  useUpdateMilestoneMutation,
  useDeleteMilestoneMutation,
  useCreateDeliverableMutation,
  useUpdateDeliverableMutation,
  useDeleteDeliverableMutation,
  useAddProjectContactMutation,
  useUpdateProjectContactMutation,
  useRemoveProjectContactMutation,
} = projectsApi;
